import clsx from "clsx";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSearchParamsOfBooking } from "../../hooks/searchParams/useSearchParamsOfBooking";
import { useBookingState } from "../../hooks/useBookingState";
import { bookingActions } from "../../redux/slices/bookingSlice";
import { extractInvalidAgeProductIds } from "../../utils/bookingUtils";
import {
  parseFreeForAges,
  ProdAttrUtils,
} from "../../utils/productAttributeUtils";
import { BookingCardProdaRow } from "../booking-card/BookingCardProdaRow";
import Icon from "../image/Icon";
import { icClose, icCloseRed, icNew } from "../image/image-src";
import { checkCanEdit } from "../../pages/Pass/components/SelectOptionSection/PassProductListPicker";
import { useTranslationEx } from "../../hooks/useTranslationEx";

const BookingStateValueInfo = ({
  booking,
  vari,
  readonly = false,
}: {
  prevBooking?: BookingState;
  booking: BookingState;
  vari: "BOOKING_CARD" | "CART";
  readonly?: boolean;
}) => {
  if (vari === "BOOKING_CARD" || booking.product?.productType === "PASS") {
    const invalidAgeProductIds = extractInvalidAgeProductIds(booking);
    const prevBooking = booking.prevBookingState;
    const ls = [
      ...[booking, ...(booking.childrenLs ?? [])],
      ...[prevBooking, ...(prevBooking?.childrenLs ?? [])]
        .filter((x) => x)
        .map((x) => ({
          ...x,
        })),
    ] as BookingState[];
    const isPass = booking.product?.productType === "PASS";

    return (
      <>
        {ls
          // distict by product id
          .filter(
            (x, i, arr) =>
              arr.findIndex((y) => y.product?.id === x.product?.id) === i,
          )
          .filter((x) => x.product?.productType !== "PASS")
          .sort((a, b) => {
            const f1 = (x: BookingState) => (x.canCancel === undefined ? 1 : 0);
            const f2 = (x: BookingState) => (x.isPrev ? 1 : 0);
            return f1(a) - f1(b) || f2(a) - f2(b);
          })
          .map((x, i) => {
            const newlyAdded =
              isPass &&
              !booking?.isPrev &&
              !prevBooking?.childrenLs?.some(
                (y) => y.product?.id === x.product?.id,
              );
            return (
              <TtRow
                key={i}
                vari={vari}
                booking={x}
                invalidProduct={invalidAgeProductIds.includes(
                  x.product?.id ?? 0,
                )}
                readonly={readonly}
                newlyAdded={newlyAdded}
              />
            );
          })}
      </>
    );
  } else {
    return (
      <div>
        <BookingCardProdaRow booking={booking} vari="GRAY" />
      </div>
    );
  }
};

const TtRow = ({
  booking,
  vari,
  invalidProduct,
  readonly = false,
  newlyAdded = false,
}: {
  booking: BookingState;
  vari: "BOOKING_CARD" | "CART";
  invalidProduct?: boolean;
  readonly?: boolean;
  newlyAdded?: boolean;
}) => {
  const { t } = useTranslation();
  const { t2 } = useTranslationEx();
  const { isUpgrade, isEdit } = useSearchParamsOfBooking();
  const [isMoreInfoOpen, setIsMoreInfoOpen] = React.useState(false);
  const dispatch = useDispatch();
  const bookingState = useBookingState({
    mode: "FORM",
  });

  const ageLs = ProdAttrUtils.parseAllChildAges(bookingState);
  const product = booking?.product;

  const freeForAgeText = useMemo(() => {
    return !!product?.freeForAges &&
      ageLs.find((x) => parseFreeForAges(product?.freeForAges).includes(+x)) !==
        undefined
      ? (product.freeForAges ?? "").replaceAll(",", ", ").replaceAll("_", "-")
      : false;
  }, [product?.freeForAges, ageLs]);

  const canEdit = checkCanEdit({
    bookingState,
    isSelected: true,
    isUpgrade,
    isEdit,
    productId: booking?.product?.id,
  });
  const willBeRemoved = isEdit && !!booking?.isPrev;
  const notEditable = (isEdit || isUpgrade) && !canEdit;
  return (
    <div className="item flex w-full items-start gap-1.5">
      <button
        className="wei-1"
        type="button"
        onClick={() => setIsMoreInfoOpen(!isMoreInfoOpen)}
      >
        <img
          className={clsx(
            "h-5 w-5",
            !willBeRemoved && notEditable && "opacity-40",
            {
              "scale-y-[-1]": !isMoreInfoOpen,
            },
          )}
          src={`../../../assets/icons/up.svg`}
          alt=""
        />
      </button>
      <div className="w-full">
        <p className="w-full text-[0.875rem] font-normal leading-[1.375rem]">
          <span
            className={clsx(
              "flex w-full",
              willBeRemoved
                ? "text-[#BFC5C9] line-through"
                : notEditable
                  ? "text-[#BFC5C9]"
                  : invalidProduct
                    ? "text-[#BFC5C9] line-through"
                    : "text-[#5E6D77]",
            )}
          >
            <span>
              <span
                className={clsx((isEdit || isUpgrade) && newlyAdded && "mr-2")}
              >
                {t2(booking?.product?.name)}
              </span>
              {(isEdit || isUpgrade) && newlyAdded && (
                <Icon className="-mt-1 inline h-4" src={icNew} />
              )}
            </span>
            {!readonly &&
              !notEditable &&
              !willBeRemoved &&
              vari === "BOOKING_CARD" &&
              bookingState?.product?.productType === "PASS" && (
                <button
                  className="wei-1 flex justify-end"
                  type="button"
                  onClick={() => {
                    const productId = booking?.product?.id;
                    productId &&
                      dispatch(
                        bookingActions.deSelectChildProducts({
                          productIds: [productId],
                        }),
                      );
                  }}
                >
                  <Icon
                    src={invalidProduct ? icCloseRed : icClose}
                    className="size-5"
                  />
                </button>
              )}
          </span>
          {willBeRemoved && (
            <span className="block text-xs font-normal leading-[1.375rem] text-[#DA3838]">
              *{t("This product will be removed after new selection")}
            </span>
          )}
          {invalidProduct && (
            <span className="block text-xs font-normal leading-[1.375rem] text-[#DA3838]">
              *This product is removed as entry allowed from age{" "}
              <b className="font-bold">{booking?.product?.requiredAge}</b>
            </span>
          )}
          {freeForAgeText && (
            <span className="block text-xs font-normal leading-[1.375rem] text-[#007AFF]">
              {t("FREE for children ages ", { age: freeForAgeText })}
            </span>
          )}
        </p>
        {isMoreInfoOpen && (
          <BookingCardProdaRow booking={booking} vari="BLUE" />
        )}
      </div>
    </div>
  );
};

export default BookingStateValueInfo;
